import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  FormText,
  TabContent,
  TabPane,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useFormik } from "lib/formik"
import Select from "components/Shared/TranslatedSelect"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  saCreateTenant,
  saGetTenantDetails,
  saGetTenantSubscription,
  saUpdateTenant,
  saGetAllPlans,
  saAssignTenantPlan,
} from "store/actions/superAdminAction"
import { getAllCountries } from "store/actions/countriesAction"
import { clearTenantDetails } from "store/reducers/superAdminReducer"
import { showAlert } from "store/reducers/alertReducer"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import { getCurrencyLabel } from "utils/currency"
import isPermissionEnabled from "common/isPermissionEnabled"
import {
  buildTenantFormSchema,
  applyTenantApiError,
  isTenantActionSuccess,
} from "utils/tenantValidation"
import { EMAIL_TOTAL_MAX_LENGTH } from "utils/emailValidation"
import ConfirmModal from "components/Common/ConfirmModal"
import TenantDatabaseMigrationPanel from "./TenantDatabaseMigrationPanel"
import TenantBrandsTab from "./TenantBrandsTab"
import TenantOtpLengthFields, {
  normalizeOtpLengthValues,
} from "components/SuperAdmin/TenantOtpLengthFields"
import TenantOtpRolloutMetricsCard from "components/SuperAdmin/TenantOtpRolloutMetricsCard"

const mapTenantDetailsToCountryIds = details => {
  if (!details || typeof details !== "object") return []
  if (Array.isArray(details.countryIds)) {
    return details.countryIds.map(id => String(id)).filter(Boolean)
  }
  if (Array.isArray(details.countries)) {
    return details.countries
      .map(c =>
        typeof c === "string" || typeof c === "number"
          ? String(c)
          : c && c._id != null
            ? String(c._id)
            : ""
      )
      .filter(Boolean)
  }
  return []
}

const isPlanInactiveError = message =>
  typeof message === "string" && /inactive/i.test(message)

const getPlanAssignmentFailureMessage = (responsePayload, t, isEditMode) => {
  const apiMessage = responsePayload?.msg || responsePayload?.message
  if (isPlanInactiveError(apiMessage)) {
    return t("The selected plan is inactive. Please choose an active plan.")
  }

  return apiMessage
    ? t(apiMessage)
    : isEditMode
      ? t("Tenant updated, but plan assignment failed. Please assign it from tenant details.")
      : t("Tenant created, but plan assignment failed. Please assign it from tenant details.")
}

const AddEditTenant = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const isEdit = mode === "edit"
  document.title = isEdit
    ? t("Edit Tenant | Super Admin")
    : t("Add Tenant | Super Admin")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const tenantId = new URLSearchParams(location.search).get("id")

  const { tenantsLoading, tenantActionLoading, tenantDetails, tenantSubscription, plans } =
    useSelector(state => state.SuperAdmin)
  const { countries: adminCountries, loading: countriesLoading } = useSelector(
    state => state.Countries
  )
  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const canCreateTenants = isPermissionEnabled(
    userRole,
    104,
    "sa_tenants_2",
    accountType,
    accountScope
  )
  const canEditTenants = isPermissionEnabled(
    userRole,
    104,
    "sa_tenants_3",
    accountType,
    accountScope
  )

  const [showDedicatedDbModal, setShowDedicatedDbModal] = useState(false)
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null)
  const [activeTab, setActiveTab] = useState("details")

  const tenantDatabase = tenantDetails?.database || {}
  const dedicatedDbLocked =
    Boolean(tenantDetails?.hasSeparateDb) &&
    String(tenantDatabase?.status || "").toLowerCase() === "active"
  const dedicatedDbFailed =
    Boolean(tenantDetails?.hasSeparateDb) &&
    String(tenantDatabase?.status || "").toLowerCase() === "failed"

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (isEdit && !canEditTenants) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (!isEdit && !canCreateTenants) {
      navigateToAccessibleRoute(navigate)
    }
  }, [
    isSuperAdminSession,
    isEdit,
    canCreateTenants,
    canEditTenants,
    navigate,
  ])

  useEffect(() => {
    dispatch(saGetAllPlans({ status: "active", all: true }))
    if (isEdit && tenantId) {
      dispatch(saGetTenantDetails({ id: tenantId }))
      dispatch(saGetTenantSubscription({ id: tenantId }))
    }
    return () => {
      dispatch(clearTenantDetails())
    }
  }, [dispatch, isEdit, tenantId])

  useEffect(() => {
    dispatch(
      getAllCountries({
        perPage: 500,
        pageNo: 1,
        status: 2,
        orderBy: 1,
        keyword: "",
      })
    )
  }, [dispatch])

  const tenantCountryOptions = useMemo(
    () =>
      (Array.isArray(adminCountries) ? adminCountries : []).map(c => ({
        value: c._id,
        label: language === "sa" ? c.nameAr || c.name : c.name || c.nameAr,
      })),
    [adminCountries, language]
  )

  const mapTenantStatusToFormValue = status => {
    if (status === 1) return "active"
    if (status === 0) return "inactive"
    if (status === 3) return "suspended"
    if (status === 4) return "pending"
    return status || "active"
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: tenantDetails.name || "",
      slug: tenantDetails.slug || "",
      adminEmail: tenantDetails.adminEmail || "",
      domain: tenantDetails.domain || "",
      useCustomLogin: Boolean(tenantDetails.useCustomLogin),
      hasWebsite: Boolean(
        tenantDetails.hasWebsite || tenantDetails.websiteDomain
      ),
      websiteDomain: tenantDetails.websiteDomain || "",
      hasQrWebsite: Boolean(
        tenantDetails.hasQrWebsite || tenantDetails.qrWebsiteDomain
      ),
      qrWebsiteDomain: tenantDetails.qrWebsiteDomain || "",
      hasCustomerAndroidApp: Boolean(tenantDetails.hasCustomerAndroidApp),
      hasCustomerIosApp: Boolean(tenantDetails.hasCustomerIosApp),
      hasRiderAndroidApp: Boolean(tenantDetails.hasRiderAndroidApp),
      hasRiderIosApp: Boolean(tenantDetails.hasRiderIosApp),
      contactEmail: tenantDetails.contactEmail || "",
      status: mapTenantStatusToFormValue(tenantDetails.status),
      planId:
        tenantSubscription?.plan?._id ||
        tenantSubscription?.planId?._id ||
        tenantSubscription?.planId ||
        tenantDetails.planId ||
        tenantDetails.subscription?.plan?._id ||
        tenantDetails.subscription?.planId?._id ||
        tenantDetails.subscription?.planId ||
        "",
      planAssignmentMode: "comped",
      planPaymentReference: "",
      countryIds: mapTenantDetailsToCountryIds(tenantDetails),
      hasSeparateDb: Boolean(tenantDetails.hasSeparateDb),
      orderPrefix: tenantDetails.orderNumbering?.prefix || "",
      orderSerialPadding: tenantDetails.orderNumbering?.serialPadding || 13,
      accountId: tenantDetails.accountId || "",
      // TEMPORARY — 4 → 6 digit OTP rollout switches (super admin only)
      otpLengthSettings: normalizeOtpLengthValues(tenantDetails.otpLengthSettings),
    },
    validationSchema: buildTenantFormSchema(t, { isEdit }),
    onSubmit: values => {
      const enablingDedicatedOnExisting =
        isEdit &&
        values.hasSeparateDb &&
        !tenantDetails.hasSeparateDb

      if (enablingDedicatedOnExisting) {
        setPendingSubmitValues(values)
        setShowDedicatedDbModal(true)
        return
      }

      submitTenantForm(values)
    },
  })

  const submitTenantForm = (
    values,
    { confirmedDedicatedDbMigration = false, startDataMigration = false } = {}
  ) => {
      const payload = { ...values }
      if (isEdit) {
        const {
          planId,
          countryIds,
          planAssignmentMode: _planAssignmentMode,
          planPaymentReference: _planPaymentReference,
          ...rest
        } = payload
        const updatePayload = {
          ...rest,
          id: tenantId,
          countryIds: Array.isArray(countryIds) ? countryIds : [],
          ...(values.hasSeparateDb !== undefined && {
            hasSeparateDb: Boolean(values.hasSeparateDb),
          }),
          ...(confirmedDedicatedDbMigration && {
            confirmedDedicatedDbMigration: true,
          }),
          ...(startDataMigration && {
            startDataMigration: true,
          }),
        }
        dispatch(saUpdateTenant(updatePayload)).then(res => {
          if (isTenantActionSuccess(res)) {
            const navigateAfterUpdate = () => {
              if (startDataMigration) {
                dispatch(
                  showAlert({
                    type: "success",
                    message: t(
                      "Dedicated database provisioned. Data migration is running in the background."
                    ),
                  })
                )
                navigate(`${ROUTES.SA_TENANT_DETAILS}?id=${tenantId}`)
                return
              }

              navigate(ROUTES.SA_TENANTS)
            }

            if (planId) {
              dispatch(
                saAssignTenantPlan({
                  id: tenantId,
                  planId,
                  assignmentMode: values.planAssignmentMode || "comped",
                  ...(values.planAssignmentMode === "manual_paid" &&
                  values.planPaymentReference
                    ? { paymentReference: values.planPaymentReference }
                    : {}),
                })
              ).then(
                assignRes => {
                  if (assignRes?.payload?.status) {
                    dispatch(
                      showAlert({
                        type: "success",
                        message: t("Tenant updated and plan assigned successfully"),
                      })
                    )
                  } else {
                    dispatch(
                      showAlert({
                        type: "warning",
                        message: getPlanAssignmentFailureMessage(
                          assignRes?.payload,
                          t,
                          true
                        ),
                      })
                    )
                  }
                  navigateAfterUpdate()
                }
              )
              return
            }

            dispatch(
              showAlert({
                type: "success",
                message: startDataMigration
                  ? t(
                      "Dedicated database provisioned. Data migration is running in the background."
                    )
                  : t("Tenant updated successfully"),
              })
            )
            navigateAfterUpdate()
            return
          }

          applyTenantApiError(res?.payload, {
            formik,
            dispatch,
            showAlert,
            t,
          })
        })
      } else {
        const {
          countryIds,
          planAssignmentMode,
          planPaymentReference,
          ...rest
        } = payload
        const createPayload = {
          ...rest,
          ...(values.hasSeparateDb && { hasSeparateDb: true }),
          ...(Array.isArray(countryIds) && countryIds.length > 0
            ? { countryIds }
            : {}),
          ...(values.planId
            ? {
                planId: values.planId,
                assignmentMode: planAssignmentMode || "comped",
                ...(planAssignmentMode === "manual_paid" && planPaymentReference
                  ? { paymentReference: planPaymentReference }
                  : {}),
              }
            : {}),
        }
        dispatch(saCreateTenant(createPayload)).then(res => {
          if (isTenantActionSuccess(res)) {
            const hasBilling = Boolean(res?.payload?.billing?.subscription)
            const createSuccessMessage =
              res?.payload?.msg || res?.payload?.message
            dispatch(
              showAlert({
                type: hasBilling ? "success" : values.planId ? "warning" : "success",
                message: hasBilling
                  ? createSuccessMessage
                    ? t(createSuccessMessage)
                    : t(
                        "Tenant created, tenant admin invited, and plan assigned successfully"
                      )
                  : values.planId
                    ? getPlanAssignmentFailureMessage(res?.payload, t, false)
                    : t("Tenant created successfully and tenant admin invited"),
              })
            )
            navigate(ROUTES.SA_TENANTS)
            return
          }

          applyTenantApiError(res?.payload, {
            formik,
            dispatch,
            showAlert,
            t,
          })
        })
      }
  }

  const handleDedicatedDbConfirm = () => {
    if (pendingSubmitValues) {
      submitTenantForm(pendingSubmitValues, {
        confirmedDedicatedDbMigration: true,
        startDataMigration: true,
      })
    }
    setShowDedicatedDbModal(false)
    setPendingSubmitValues(null)
  }

  const formatProvisionedDate = value => {
    if (!value) return "—"
    try {
      return new Date(value).toLocaleString()
    } catch (_err) {
      return String(value)
    }
  }

  const activePlans = useMemo(
    () =>
      (Array.isArray(plans) ? plans : []).filter(
        plan => String(plan?.status || "").toLowerCase() === "active"
      ),
    [plans]
  )

  if (isEdit && tenantsLoading && !tenantDetails._id) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <h4 className="mb-3">
            {isEdit ? t("Edit Tenant") : t("Add Tenant")}
          </h4>

          {isEdit && (
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={activeTab === "details" ? "active" : ""}
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveTab("details")}
                >
                  {t("Tenant Details")}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === "brands" ? "active" : ""}
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveTab("brands")}
                >
                  {t("Brands")}
                </NavLink>
              </NavItem>
            </Nav>
          )}

          <TabContent activeTab={activeTab}>
            <TabPane tabId="brands">
              {isEdit && activeTab === "brands" && tenantId && (
                <Card>
                  <CardBody>
                    <TenantBrandsTab tenantId={tenantId} />
                  </CardBody>
                </Card>
              )}
            </TabPane>
            <TabPane tabId="details">
            <Card>
            <CardBody>
              <Form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Tenant Name")} *</Label>
                      <Input
                        name="name"
                        placeholder={t("Enter tenant name")}
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.name && !!formik.errors.name
                        }
                      />
                      <FormFeedback>{formik.errors.name}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Domain")} *</Label>
                      <Input
                        name="domain"
                        placeholder={t("e.g. acme.halfmillion.app")}
                        value={formik.values.domain}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.domain && !!formik.errors.domain
                        }
                      />
                      <FormFeedback>{formik.errors.domain}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup check className="mb-2">
                      <Input
                        type="checkbox"
                        id="useCustomLogin"
                        name="useCustomLogin"
                        checked={Boolean(formik.values.useCustomLogin)}
                        onChange={e => {
                          formik.setFieldValue("useCustomLogin", e.target.checked)
                        }}
                      />
                      <Label check for="useCustomLogin" className="ms-2">
                        {t("Use custom login")}
                      </Label>
                      <FormText color="muted" className="d-block ms-4">
                        {t(
                          "When enabled, the tenant portal domain shows a branded login page as the home page instead of platform onboarding."
                        )}
                      </FormText>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Slug")} *</Label>
                      <Input
                        name="slug"
                        placeholder={t("e.g. acme-store")}
                        value={formik.values.slug}
                        onChange={e => {
                          formik.setFieldValue(
                            "slug",
                            e.target.value.toLowerCase()
                          )
                        }}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.slug && !!formik.errors.slug
                        }
                      />
                      <FormFeedback>{formik.errors.slug}</FormFeedback>
                    </FormGroup>
                  </Col>

                  {isEdit ? (
                    <Col md={6}>
                      <FormGroup>
                        <Label>{t("Account ID")} *</Label>
                        <Input
                          name="accountId"
                          placeholder={t("e.g. ABC12345")}
                          maxLength={32}
                          value={formik.values.accountId}
                          onChange={e => {
                            formik.setFieldValue(
                              "accountId",
                              e.target.value
                                .toUpperCase()
                                .replace(/[^A-Z0-9]/g, "")
                            )
                          }}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.accountId &&
                            !!formik.errors.accountId
                          }
                        />
                        <FormText>
                          {t(
                            "Unique identifier used by the rider app to resolve the tenant during login."
                          )}
                        </FormText>
                        <FormFeedback>{formik.errors.accountId}</FormFeedback>
                      </FormGroup>
                    </Col>
                  ) : (
                    <Col md={6}>
                      <FormGroup>
                        <Label>{t("Account ID")}</Label>
                        <Input value={t("Auto-generated on create")} disabled readOnly />
                        <FormText>
                          {t(
                            "A unique account ID is generated automatically when the tenant is created."
                          )}
                        </FormText>
                      </FormGroup>
                    </Col>
                  )}

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Contact Email")} *</Label>
                      <Input
                        name="contactEmail"
                        type="email"
                        maxLength={EMAIL_TOTAL_MAX_LENGTH}
                        placeholder={t("admin@tenant.com")}
                        value={formik.values.contactEmail}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.contactEmail &&
                          !!formik.errors.contactEmail
                        }
                      />
                      <FormFeedback>{formik.errors.contactEmail}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Tenant Admin Email")} *</Label>
                      <Input
                        name="adminEmail"
                        type="email"
                        maxLength={EMAIL_TOTAL_MAX_LENGTH}
                        placeholder={t("tenant-admin@company.com")}
                        value={formik.values.adminEmail}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.adminEmail &&
                          !!formik.errors.adminEmail
                        }
                      />
                      <FormFeedback>{formik.errors.adminEmail}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Order Prefix")} {!isEdit ? "*" : ""}</Label>
                      <Input
                        name="orderPrefix"
                        placeholder={t("e.g. AD")}
                        maxLength={4}
                        value={formik.values.orderPrefix}
                        onChange={e => {
                          formik.setFieldValue(
                            "orderPrefix",
                            e.target.value.toUpperCase().replace(/[^A-Z]/g, "")
                          )
                        }}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.orderPrefix &&
                          !!formik.errors.orderPrefix
                        }
                      />
                      <FormText>
                        {t(
                          "2-4 uppercase letters shown before the sequential order number (e.g. AD0000000000001)."
                        )}
                      </FormText>
                      <FormFeedback>{formik.errors.orderPrefix}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Order Serial Padding")}</Label>
                      <Input
                        name="orderSerialPadding"
                        type="number"
                        min={5}
                        max={20}
                        value={formik.values.orderSerialPadding}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.orderSerialPadding &&
                          !!formik.errors.orderSerialPadding
                        }
                      />
                      <FormText>
                        {t("Minimum 5 digits for the numeric part of the order ID.")}
                      </FormText>
                      <FormFeedback>{formik.errors.orderSerialPadding}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Status")} *</Label>
                      <Input
                        type="select"
                        name="status"
                        value={formik.values.status}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.status && !!formik.errors.status
                        }
                      >
                        <option value="active">{t("Active")}</option>
                        <option value="suspended">{t("Suspended")}</option>
                        <option value="inactive">{t("Inactive")}</option>
                        <option value="pending">{t("Pending")}</option>
                      </Input>
                      <FormFeedback>{formik.errors.status}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Initial Plan")}</Label>
                      <Input
                        type="select"
                        name="planId"
                        value={formik.values.planId}
                        onChange={formik.handleChange}
                      >
                        <option value="">{t("No plan (assign later)")}</option>
                        {activePlans.map(plan => (
                          <option key={plan._id} value={plan._id}>
                            {plan.name} — {getCurrencyLabel(plan.currency || "SAR")}{" "}
                            {Number(plan.price || 0).toFixed(2)} /{" "}
                            {plan.billingCycle === "yearly"
                              ? t("year")
                              : t("month")}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>

                  {formik.values.planId && (
                    <Col md={12}>
                      <FormGroup>
                        <Label>{t("Payment option")}</Label>
                        <div className="d-flex flex-column gap-2">
                          <FormGroup check className="mb-0">
                            <Input
                              type="radio"
                              id="create-plan-comped"
                              name="planAssignmentMode"
                              value="comped"
                              checked={formik.values.planAssignmentMode === "comped"}
                              onChange={formik.handleChange}
                            />
                            <Label check for="create-plan-comped" className="ms-1">
                              {t("Activate immediately (comped)")}
                            </Label>
                          </FormGroup>
                          <FormGroup check className="mb-0">
                            <Input
                              type="radio"
                              id="create-plan-payment-link"
                              name="planAssignmentMode"
                              value="payment_link"
                              checked={
                                formik.values.planAssignmentMode === "payment_link"
                              }
                              onChange={formik.handleChange}
                            />
                            <Label check for="create-plan-payment-link" className="ms-1">
                              {t("Send payment link to tenant admin")}
                            </Label>
                          </FormGroup>
                          <FormGroup check className="mb-0">
                            <Input
                              type="radio"
                              id="create-plan-manual-paid"
                              name="planAssignmentMode"
                              value="manual_paid"
                              checked={
                                formik.values.planAssignmentMode === "manual_paid"
                              }
                              onChange={formik.handleChange}
                            />
                            <Label check for="create-plan-manual-paid" className="ms-1">
                              {t("Mark as manually paid")}
                            </Label>
                          </FormGroup>
                        </div>
                      </FormGroup>
                      {formik.values.planAssignmentMode === "manual_paid" && (
                        <FormGroup>
                          <Label for="planPaymentReference">
                            {t("Payment reference")}
                          </Label>
                          <Input
                            id="planPaymentReference"
                            name="planPaymentReference"
                            value={formik.values.planPaymentReference}
                            onChange={formik.handleChange}
                            placeholder={t("Bank transfer, receipt #, etc.")}
                          />
                        </FormGroup>
                      )}
                    </Col>
                  )}

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Countries")}</Label>
                      <Select
                        inputId="tenant-country-ids"
                        isMulti
                        isLoading={countriesLoading}
                        isClearable
                        options={tenantCountryOptions}
                        value={tenantCountryOptions.filter(o =>
                          (formik.values.countryIds || []).includes(o.value)
                        )}
                        onChange={selected => {
                          formik.setFieldValue(
                            "countryIds",
                            selected ? selected.map(s => s.value) : []
                          )
                        }}
                        placeholder={t("Select Countries")}
                        classNamePrefix="react-select"
                        className="react-select select2-selection"
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup check className="mb-2">
                      <Input
                        type="checkbox"
                        id="hasWebsite"
                        name="hasWebsite"
                        checked={Boolean(formik.values.hasWebsite)}
                        onChange={e => {
                          const checked = e.target.checked
                          formik.setFieldValue("hasWebsite", checked)
                          if (!checked) {
                            formik.setFieldValue("websiteDomain", "")
                          }
                        }}
                      />
                      <Label check for="hasWebsite" className="ms-2">
                        {t("Enable customer website")}
                      </Label>
                      <FormText color="muted" className="d-block ms-4">
                        {t(
                          "When enabled, configure a website domain to resolve the tenant for the customer storefront."
                        )}
                      </FormText>
                    </FormGroup>
                  </Col>

                  {formik.values.hasWebsite && (
                    <Col md={6}>
                      <FormGroup>
                        <Label>{t("Website Domain")} *</Label>
                        <Input
                          name="websiteDomain"
                          placeholder={t("e.g. shop.acme.com")}
                          value={formik.values.websiteDomain}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.websiteDomain &&
                            !!formik.errors.websiteDomain
                          }
                        />
                        <FormText>
                          {t(
                            "Customer website hostname used to resolve tenant ID for the storefront."
                          )}
                        </FormText>
                        <FormFeedback>{formik.errors.websiteDomain}</FormFeedback>
                      </FormGroup>
                    </Col>
                  )}

                  <Col md={12}>
                    <FormGroup check className="mb-2">
                      <Input
                        type="checkbox"
                        id="hasQrWebsite"
                        name="hasQrWebsite"
                        checked={Boolean(formik.values.hasQrWebsite)}
                        onChange={e => {
                          const checked = e.target.checked
                          formik.setFieldValue("hasQrWebsite", checked)
                          if (!checked) {
                            formik.setFieldValue("qrWebsiteDomain", "")
                          }
                        }}
                      />
                      <Label check for="hasQrWebsite" className="ms-2">
                        {t("Enable QR website")}
                      </Label>
                      <FormText color="muted" className="d-block ms-4">
                        {t(
                          "When enabled, configure a QR website domain to resolve the tenant for the QR ordering site."
                        )}
                      </FormText>
                    </FormGroup>
                  </Col>

                  {formik.values.hasQrWebsite && (
                    <Col md={6}>
                      <FormGroup>
                        <Label>{t("QR Website Domain")} *</Label>
                        <Input
                          name="qrWebsiteDomain"
                          placeholder={t("e.g. qr.acme.com")}
                          value={formik.values.qrWebsiteDomain}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.qrWebsiteDomain &&
                            !!formik.errors.qrWebsiteDomain
                          }
                        />
                        <FormText>
                          {t(
                            "QR website hostname used to resolve tenant ID for the QR ordering shell."
                          )}
                        </FormText>
                        <FormFeedback>{formik.errors.qrWebsiteDomain}</FormFeedback>
                      </FormGroup>
                    </Col>
                  )}

                  <Col md={12}>
                    <p className="text-muted small mb-2 mt-2">
                      {t("Mobile apps available for this tenant")}
                    </p>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mb-2">
                      <Input
                        type="checkbox"
                        id="hasCustomerAndroidApp"
                        name="hasCustomerAndroidApp"
                        checked={Boolean(formik.values.hasCustomerAndroidApp)}
                        onChange={e =>
                          formik.setFieldValue(
                            "hasCustomerAndroidApp",
                            e.target.checked
                          )
                        }
                      />
                      <Label check for="hasCustomerAndroidApp" className="ms-2">
                        {t("Customer Android app")}
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mb-2">
                      <Input
                        type="checkbox"
                        id="hasCustomerIosApp"
                        name="hasCustomerIosApp"
                        checked={Boolean(formik.values.hasCustomerIosApp)}
                        onChange={e =>
                          formik.setFieldValue("hasCustomerIosApp", e.target.checked)
                        }
                      />
                      <Label check for="hasCustomerIosApp" className="ms-2">
                        {t("Customer iOS app")}
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mb-2">
                      <Input
                        type="checkbox"
                        id="hasRiderAndroidApp"
                        name="hasRiderAndroidApp"
                        checked={Boolean(formik.values.hasRiderAndroidApp)}
                        onChange={e =>
                          formik.setFieldValue(
                            "hasRiderAndroidApp",
                            e.target.checked
                          )
                        }
                      />
                      <Label check for="hasRiderAndroidApp" className="ms-2">
                        {t("Rider Android app")}
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mb-2">
                      <Input
                        type="checkbox"
                        id="hasRiderIosApp"
                        name="hasRiderIosApp"
                        checked={Boolean(formik.values.hasRiderIosApp)}
                        onChange={e =>
                          formik.setFieldValue("hasRiderIosApp", e.target.checked)
                        }
                      />
                      <Label check for="hasRiderIosApp" className="ms-2">
                        {t("Rider iOS app")}
                      </Label>
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <TenantOtpLengthFields
                      value={formik.values.otpLengthSettings}
                      onChange={next => formik.setFieldValue("otpLengthSettings", next)}
                      showRiderFlows={Boolean(
                        formik.values.hasRiderAndroidApp || formik.values.hasRiderIosApp
                      )}
                    />
                  </Col>

                  <Col md={12}>
                    <FormGroup check className="mb-2">
                      <Input
                        type="checkbox"
                        id="hasSeparateDb"
                        name="hasSeparateDb"
                        checked={Boolean(formik.values.hasSeparateDb)}
                        disabled={dedicatedDbLocked}
                        onChange={e => {
                          formik.setFieldValue("hasSeparateDb", e.target.checked)
                        }}
                      />
                      <Label check for="hasSeparateDb" className="ms-2">
                        {t("Provision dedicated database for this tenant")}
                      </Label>
                      <FormText color="muted" className="d-block ms-4">
                        {t(
                          "Creates an isolated MongoDB database on the same cluster. Cannot be reversed without migration."
                        )}
                      </FormText>
                    </FormGroup>
                  </Col>

                  {(dedicatedDbLocked || dedicatedDbFailed) && (
                    <Col md={12}>
                      <div className="border rounded p-3 bg-light mb-3">
                        <h6 className="mb-2">{t("Dedicated database")}</h6>
                        <Row>
                          <Col md={4}>
                            <small className="text-muted d-block">{t("Database name")}</small>
                            <span>{tenantDatabase.databaseName || "—"}</span>
                          </Col>
                          <Col md={4}>
                            <small className="text-muted d-block">{t("Status")}</small>
                            <span className="text-capitalize">
                              {tenantDatabase.status || "—"}
                            </span>
                          </Col>
                          <Col md={4}>
                            <small className="text-muted d-block">{t("Provisioned at")}</small>
                            <span>{formatProvisionedDate(tenantDatabase.provisionedAt)}</span>
                          </Col>
                        </Row>
                        {dedicatedDbFailed && tenantDatabase.provisioningError && (
                          <p className="text-danger small mb-0 mt-2">
                            {tenantDatabase.provisioningError}
                          </p>
                        )}
                        {dedicatedDbLocked && !tenantDatabase.cutoverComplete && (
                          <p className="text-warning small mb-0 mt-2">
                            {t(
                              "Dedicated database is provisioned. Tenant continues on shared database until migration cutover."
                            )}
                          </p>
                        )}
                      </div>
                    </Col>
                  )}
                </Row>

                {isEdit && dedicatedDbLocked ? (
                  <TenantDatabaseMigrationPanel
                    tenantId={tenantId}
                    tenantDetails={tenantDetails}
                  />
                ) : null}

                <div className="d-flex gap-2 mt-2">
                  <Button
                    color="primary"
                    type="submit"
                    disabled={tenantActionLoading}
                  >
                    {tenantActionLoading ? (
                      <Spinner size="sm" />
                    ) : isEdit ? (
                      t("Save Changes")
                    ) : (
                      t("Create Tenant")
                    )}
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    type="button"
                    onClick={() => navigate(ROUTES.SA_TENANTS)}
                  >
                    {t("Cancel")}
                  </Button>
                </div>
              </Form>
              {isEdit && tenantId ? (
                <div className="mt-4">
                  <TenantOtpRolloutMetricsCard tenantId={tenantId} />
                </div>
              ) : null}
            </CardBody>
          </Card>

          </TabPane>
          </TabContent>

          <ConfirmModal
            show={showDedicatedDbModal}
            onCloseClick={() => {
              setShowDedicatedDbModal(false)
              setPendingSubmitValues(null)
            }}
            onDeleteClick={handleDedicatedDbConfirm}
            buttonText={t("Provision and start migration")}
            bodyTitle={t(
              "This tenant already has data in the shared database. Enabling a dedicated database will provision a new MongoDB database and copy tenant data in the background. The tenant will keep using the shared database until you complete cutover from the migration panel."
            )}
            isLoading={tenantActionLoading}
          />
        </Container>
      </div>
    </React.Fragment>
  )
}

export default AddEditTenant
